import { z } from 'zod/v3';
import {
  TaskSourceSchema,
  TaskStatusSchema,
  TaskResolutionOutcomeSchema,
  PrioritySchema,
} from '../generated/enums';

/**
 * RFC-205: native Task DTO — the generic work spine (store-of-record for
 * work with no domain home: email intake, ad-hoc requests, follow-ups,
 * findings triaged into action).
 *
 * Work status ≠ compliance status: `status` is a kanban lane and never
 * advances a CES / finding lifecycle (RFC-205 §6.5 Rule 2).
 *
 * Enums come from the generated Prisma enums (source of truth) —
 * TaskSourceSchema / TaskStatusSchema / PrioritySchema.
 *
 * Named NativeTask* because `TaskDTO` is already taken by the CES board-row
 * shape in guided-template.dto.ts ("task" = CES there). "Native task" is the
 * RFC-205 term for a real Task row, vs a projected CES/finding.
 */

/** RFC-206 §5.4 — the stamped Vera triage suggestion (Task.triageSuggestion). */
export const TriageSuggestionDTOSchema = z.object({
  triggerId: z.string(),
  workflowId: z.string(),
  workflowName: z.string(),
  /// Coarse label (display) derived from `confidenceScore`.
  confidence: z.enum(['high', 'medium', 'low']),
  /// #1439 — raw 0–1 match confidence. ≥0.75 auto-executes an opted-in
  /// procedure; below escalates to admin approval. Nullish for legacy rows
  /// stamped before the numeric score existed.
  confidenceScore: z.number().min(0).max(1).nullish(),
  /// Shown to the human: WHY it matched (+ runners-up when several matched).
  rationale: z.string(),
  /// Drafted against the Procedure's declared params; pre-fills the dialog.
  draftInputs: z.record(z.unknown()),
  suggestedAt: z.string(), // ISO
});
export type TriageSuggestionDTO = z.infer<typeof TriageSuggestionDTOSchema>;

/** RFC-206 §5.7 — one selectable procedure in the graduation dialog. */
export const TriageCandidateParamDTOSchema = z.object({
  name: z.string(),
  type: z.string(),
  description: z.string(),
  required: z.boolean(),
  defaultValue: z.unknown().optional(),
});
export type TriageCandidateParamDTO = z.infer<typeof TriageCandidateParamDTOSchema>;

export const TriageCandidateDTOSchema = z.object({
  triggerId: z.string(),
  workflowId: z.string(),
  name: z.string(),
  conditions: z.string(),
  /// Declared non-secret params — drives the RunbookInputsForm in the dialog.
  params: z.array(TriageCandidateParamDTOSchema),
});
export type TriageCandidateDTO = z.infer<typeof TriageCandidateDTOSchema>;

export const NativeTaskAssigneeDTOSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  /// Agent/service-account (e.g. Vera) → render the robot avatar, not initials.
  isServiceAccount: z.boolean().nullable().optional(),
});
export type NativeTaskAssigneeDTO = z.infer<typeof NativeTaskAssigneeDTOSchema>;

export const NativeTaskDTOSchema = z.object({
  id: z.string(),
  /// Numeric per-workspace sequence; rendered as `TASK-${code}`.
  code: z.number(),
  displayCode: z.string(), // "TASK-42"
  title: z.string(),
  description: z.string().nullable(),
  status: TaskStatusSchema,
  priority: PrioritySchema,
  labels: z.array(z.string()),
  dueDate: z.string().nullable(), // ISO
  assignedToId: z.string().nullable(),
  assignedTo: NativeTaskAssigneeDTOSchema.nullable(),
  source: TaskSourceSchema,
  sourceDetail: z.string().nullable(),
  createdById: z.string().nullable(),
  /// Optional domain links (references, not ownership)
  cesId: z.string().nullable(),
  remediationTicketId: z.string().nullable(),
  riskItemId: z.string().nullable(),
  /// Graduation (stamped only after a confirmed ProcedureEventTrigger fire)
  graduatedToWorkflowExecutionId: z.string().nullable(),
  graduatedAt: z.string().nullable(), // ISO
  procedureEventType: z.string().nullable(),
  /// RFC-206 §5.4 — Vera's conditions-matched suggestion (suggestion-only;
  /// a human click on the graduation dialog is the only path to dispatch).
  triageSuggestion: TriageSuggestionDTOSchema.nullable(),
  /// RFC-206 §6.3 — typed terminal outcome (evidence-eligibility input).
  resolutionOutcome: TaskResolutionOutcomeSchema.nullable(),
  /// External ticket mirror (same vocabulary as the CES mirror)
  externalTicketSource: z.string().nullable(),
  externalTicketId: z.string().nullable(),
  externalTicketUrl: z.string().nullable(),
  resolvedAt: z.string().nullable(), // ISO
  createdAt: z.string(), // ISO
  updatedAt: z.string(), // ISO
});
export type NativeTaskDTO = z.infer<typeof NativeTaskDTOSchema>;

export const NativeTaskCountsDTOSchema = z.object({
  open: z.number(),
  inProgress: z.number(),
  overdue: z.number(),
  resolved: z.number(),
  byStatus: z.record(z.number()),
});
export type NativeTaskCountsDTO = z.infer<typeof NativeTaskCountsDTOSchema>;

/** Board-wide workload totals, independent of the paginated task window. */
export const TaskWorkloadCountsDTOSchema = z.object({
  byAssignee: z.record(z.number()),
  byOwner: z.record(z.number()),
  assigneeLabels: z.record(z.string()),
  ownerLabels: z.record(z.string()),
});
export type TaskWorkloadCountsDTO = z.infer<typeof TaskWorkloadCountsDTOSchema>;

export const NativeTaskListResponseDTOSchema = z.object({
  items: z.array(NativeTaskDTOSchema),
  /// Cursor for the native-task source (RFC-205 §4.1 per-source pagination).
  nextCursor: z.string().nullable(),
  total: z.number(),
});
export type NativeTaskListResponseDTO = z.infer<typeof NativeTaskListResponseDTOSchema>;

/** Live policy references derived from a task's remediation finding. */
export type TaskPolicyContextDTO = {
  question: string | null;
  requirement: string | null;
  policies: Array<{ id: string; slug: string; title: string }>;
};
